"use client";

import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useTransform, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { HeroSection, getStrapiMedia } from "@/lib/strapi";
import { usePreloader } from "@/context/PreloaderContext";

interface HeroProps {
  data: HeroSection;
}

/**
 * Константы настроек Tag Cloud
 */
const cloudConfig = {
  minOpacity: 0.20,
  maxOpacity: 0.50,
  animationDuration: 25,
  parallaxStrength: 0.03,
  fontSizeRange: [14, 28],
  containerOpacity: 0.5,
  words: [
"Перевод", "Traducere", "Translation", "Apostille", "Apostilă", "Апостиль", 
"Notary", "Notar", "Нотариус", "Documents", "Documente", "Документы", 
"Legal", "Medical", "Technical", "Urgent", "Chisinau", "Moldova",
"Contract", "Diploma", "Pașaport", "Legalizare", "Calitate", "Draft",
"Übersetzung", "Traducción", "Zertifikat", "翻訳", "翻译", "통역", 
"ISO 17100", "Localization", "Interpretariato", "Official", "Accurate", "Context"
  ]
};

/**
 * Константы настроек анимации
 */
const ANIMATION_CONFIG = {
  entrance: {
    duration: 0.8,
    ease: "easeOut" as const,
  },
  phrases: {
    interval: 3000,
    duration: 0.5,
  },
  levitation: {
    duration: 4,
    repeat: Infinity,
    ease: "easeInOut" as const,
  }
} as const;

/**
 * КОНФИГУРАЦИЯ ПЛАНШЕТА (Редактируйте здесь для настройки положения)
 */
const TABLET_LAYOUT_CONFIG = {
  // Параметры самого планшета
  container: {
    maxWidth: 620,      // Максимальная ширина контейнера (в px)
    levitationY: 15,    // На сколько пикселей планшет плавает вверх-вниз
    rotation: 3,       // Поворот всего планшета (в градусах)
  },
  // ПОДСВЕТКА ЗА ПЛАНШЕТОМ (Backlight)
  backGlow: {
    color: "rgba(37,99,235,0.9)", // Цвет свечения (blue-500 с прозрачностью)
    blur: "150px",                // Размытие краев
    spread: 1.3,                  // Масштаб свечения относительно планшета
    pulseSpeed: 3,                // Длительность пульсации в секундах
    pulseIntensity: [0.5, 1, 0.5], // Коэффициент пульсации яркости/размера
  },
  // Параметры экрана (контент ВНУТРИ планшета)
  screen: {
    perspective: "8000px", 
    top: "3%",
    left: "4%",
    width: "90%",
    height: "90%",
    rotateX: 5,
    rotateY: 0,
    rotateZ: 0,
    glowIntensity: 0.1, // Внутреннее свечение самого экрана
    verticalAlign: "center" as "center" | "start" | "end", // Позиционирование по вертикали
  },
  // ПАРАМЕТРЫ ТЕКСТА И АНИМАЦИИ НА ЭКРАНЕ
  screenText: {
    fontSize: "40px",         // Размер шрифта основного текста
    fontSizeMobile: "30px",   // Размер шрифта для мобильных
    lineHeight: "1.4",        // Межстрочный интервал
    opacity: 0.9,             // Прозрачность текста
    spacing: "space-y-4",     // Отступ между строками
    glowColor: "rgba(59,130,246,0.5)", // Цвет свечения (blue-500)
    glowRadius: "8px",        // Радиус свечения (drop-shadow)
    // ТАЙМИНГИ АНИМАЦИИ
    rotationInterval: 6000,   // Интервал смены блоков (мс)
    staggerDelay: 0.3,       // Значительный люфт между строками
  }
};

/**
 * Внутренний компонент экрана планшета с динамической ротацией
 */
/**
 * КОМПОНЕНТ ФОНОВЫХ ПОТОКОВ ДАННЫХ (Data Stream Drift)
 * Создает эффект ультра-тонких светящихся линий с ромбиками, дрейфующих на заднем плане.
 */
function DataStreamBackground() {
  const streams = [
    { id: 1, start: { x: -100, y: 100 }, end: { x: 1100, y: 700 }, duration: 20, delay: 0 },
    { id: 2, start: { x: 300, y: -100 }, end: { x: 800, y: 1100 }, duration: 25, delay: 4 },
    { id: 3, start: { x: -200, y: 400 }, end: { x: 1200, y: 800 }, duration: 18, delay: 8 },
    { id: 4, start: { x: 500, y: -100 }, end: { x: 200, y: 1100 }, duration: 22, delay: 2 },
    { id: 5, start: { x: 900, y: 200 }, end: { x: -100, y: 900 }, duration: 30, delay: 6 },
  ];

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-40">
      <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trailGradient" x1="1" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0.9)" />
            <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
          </linearGradient>
        </defs>
        <AnimatePresence>
          {streams.map((stream) => (
            <StreamGroup key={stream.id} {...stream} />
          ))}
        </AnimatePresence>
      </svg>
    </div>
  );
}

function StreamGroup({ start, end, duration, delay }: any) {
  return (
    <g>
      <StreamLine start={start} end={end} duration={duration} delay={delay} />
      <StreamLine start={start} end={end} duration={duration} delay={delay + duration / 2} />
    </g>
  );
}

function StreamLine({ start, end, duration, delay }: any) {
  return (
    <motion.g
      initial={{ x: start.x, y: start.y, opacity: 0 }}
      animate={{
        x: [start.x, end.x],
        y: [start.y, end.y],
        opacity: [0, 1, 1, 0]
      }}
      transition={{
        duration: duration,
        repeat: Infinity,
        ease: "linear",
        delay: delay,
        times: [0, 0.1, 0.9, 1]
      }}
    >
      <line
        x1="0" y1="0"
        x2={-(end.x - start.x) * 0.12}
        y2={-(end.y - start.y) * 0.12}
        stroke="url(#trailGradient)"
        strokeWidth="0.5"
        style={{ filter: "drop-shadow(0 0 2px rgba(147, 197, 253, 0.5))" }}
      />
      <path
        d="M -2.5 0 L 0 -2.5 L 2.5 0 L 0 2.5 Z"
        fill="white"
        style={{ filter: "drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))" }}
      />
    </motion.g>
  );
}

/**
 * КОМПОНЕНТ ОБЛАКА ТЕГОВ (Tag Cloud)
 * Создает эффект парящих слов на заднем плане с интерактивным параллаксом.
 */
function TagWord({ word }: { word: string }) {
  const [styles, setStyles] = useState<{top: string, left: string, fontSize: number, radius: number, dir: number} | null>(null);

  useEffect(() => {
    setStyles({
      top: Math.random() * 85 + 5 + "%",
      left: Math.random() * 85 + 5 + "%",
      fontSize: Math.floor(Math.random() * (cloudConfig.fontSizeRange[1] - cloudConfig.fontSizeRange[0]) + cloudConfig.fontSizeRange[0]),
      radius: 15 + Math.random() * 25,
      dir: Math.random() > 0.5 ? 1 : -1
    });
  }, []);

  if (!styles) return null;

  return (
    <motion.span
      className="absolute text-blue-100 font-medium whitespace-nowrap select-none pointer-events-none"
      style={{ 
        top: styles.top, 
        left: styles.left, 
        fontSize: styles.fontSize,
        textShadow: "0 0 10px rgba(191, 219, 254, 0.2)"
      }}
      animate={{
        x: [0, styles.radius * styles.dir, 0, -styles.radius * styles.dir, 0],
        y: [0, -styles.radius, 0, styles.radius, 0],
        opacity: [cloudConfig.minOpacity, cloudConfig.maxOpacity, cloudConfig.minOpacity],
        scale: [0.9, 1.1, 0.9]
      }}
      transition={{
        duration: cloudConfig.animationDuration + Math.random() * 15,
        repeat: Infinity,
        ease: "linear",
        delay: Math.random() * 10
      }}
    >
      {word}
    </motion.span>
  );
}

function TagCloud({ mouseX, mouseY }: { mouseX: any, mouseY: any }) {
  const x = useSpring(useTransform(mouseX, [-1000, 1000], [1000 * cloudConfig.parallaxStrength, -1000 * cloudConfig.parallaxStrength]), { stiffness: 40, damping: 25 });
  const y = useSpring(useTransform(mouseY, [-1000, 1000], [1000 * cloudConfig.parallaxStrength, -1000 * cloudConfig.parallaxStrength]), { stiffness: 40, damping: 25 });

  return (
    <motion.div 
      style={{ x, y }} 
      className="absolute inset-x-0 inset-y-0 z-[-1] pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: cloudConfig.containerOpacity }}
      transition={{ duration: 2 }}
    >
      {cloudConfig.words.map((word, i) => (
        <TagWord key={i} word={word} />
      ))}
    </motion.div>
  );
}

function TabletCanvas({ data }: { data: HeroSection }) {
  const [currentBlock, setCurrentBlock] = useState(0);

  // Каждые 6 секунд переключаем блок
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBlock((prev) => (prev + 1) % 3);
    }, TABLET_LAYOUT_CONFIG.screenText.rotationInterval);
    return () => clearInterval(timer);
  }, []);

  // Получаем текущий текст в зависимости от индекса
  const getCurrentText = () => {
    switch (currentBlock) {
      case 0: return data.TabletLine1;
      case 1: return data.TabletLine2;
      case 2: return data.TabletLine3;
      default: return "";
    }
  };

  // Разделяем текст на строки
  const lines = (getCurrentText() || "")
    .split('\n')
    .filter(line => line.trim() !== '');

  // Варианты для контейнера блока (AnimatePresence)
  const blockVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: {
        staggerChildren: TABLET_LAYOUT_CONFIG.screenText.staggerDelay,
        delayChildren: 0.2
      }
    },
    exit: { opacity: 0 }
  };

  // Варианты для контейнера линии (необходимы для работы staggerChildren)
  const lineVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { duration: 0.1 }
    }
  };

  // Варианты для галочки (Icon)
  const iconVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { 
      scale: [0, 1.1, 1],
      opacity: 1,
      transition: { 
        duration: 0.8,
        ease: "easeOut" as const,
        delay: 1.2 // Появляется после текста
      }
    }
  };

  // Варианты для текста
  const textVariants = {
    initial: { opacity: 0, x: -30 },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.8,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <div className="absolute inset-0 z-20 pointer-events-none" style={{ perspective: TABLET_LAYOUT_CONFIG.screen.perspective }}>
      <div 
        className={`absolute inset-0 flex flex-col p-[10%] overflow-hidden ${
          TABLET_LAYOUT_CONFIG.screen.verticalAlign === 'center' ? 'justify-center' : 
          TABLET_LAYOUT_CONFIG.screen.verticalAlign === 'end' ? 'justify-end' : 'justify-start'
        }`}
        style={{
          top: TABLET_LAYOUT_CONFIG.screen.top,
          left: TABLET_LAYOUT_CONFIG.screen.left,
          width: TABLET_LAYOUT_CONFIG.screen.width,
          height: TABLET_LAYOUT_CONFIG.screen.height,
          transform: `rotateX(${TABLET_LAYOUT_CONFIG.screen.rotateX}deg) rotateY(${TABLET_LAYOUT_CONFIG.screen.rotateY}deg) rotateZ(${TABLET_LAYOUT_CONFIG.screen.rotateZ}deg)`,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBlock}
            variants={blockVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className={`${TABLET_LAYOUT_CONFIG.screenText.spacing} font-mono text-blue-100 w-full`}
            style={{
              fontSize: TABLET_LAYOUT_CONFIG.screenText.fontSizeMobile,
              lineHeight: TABLET_LAYOUT_CONFIG.screenText.lineHeight,
              opacity: TABLET_LAYOUT_CONFIG.screenText.opacity,
            }}
          >
            <style jsx>{`
              @media (min-width: 768px) {
                .font-mono { font-size: ${TABLET_LAYOUT_CONFIG.screenText.fontSize} !important; }
              }
            `}</style>

            {lines.map((line, idx) => (
              <motion.div 
                key={`${currentBlock}-${idx}`} 
                variants={lineVariants}
                className="flex items-start gap-3"
                style={{
                  filter: `drop-shadow(0 0 ${TABLET_LAYOUT_CONFIG.screenText.glowRadius} ${TABLET_LAYOUT_CONFIG.screenText.glowColor})`
                }}
              >
                {/* Галочка */}
                <motion.div variants={iconVariants} className="mt-1 flex-shrink-0">
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    className="w-5 h-5 md:w-7 md:h-7 text-blue-400" 
                    stroke="currentColor" 
                    strokeWidth="4" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </motion.div>

                {/* Текст строки */}
                <motion.span 
                  variants={textVariants}
                  className="font-medium tracking-tight whitespace-pre-wrap pt-0.5"
                >
                  {line}
                </motion.span>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Pulsing Point (Online Status) */}
        <div className="absolute top-[5%] right-[5%] flex items-center gap-2 bg-slate-950/60 px-2 py-0.5 rounded-full border border-blue-500/20 backdrop-blur-sm">
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1], 
              opacity: [0.4, 1, 0.4] 
            }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.8)]"
          />
          <span className="text-[7px] uppercase tracking-[0.2em] font-black text-blue-300">Active</span>
        </div>
      </div>
    </div>
  );
}

export default function Hero({ data }: HeroProps) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isExiting } = usePreloader();

  // Mouse tracking for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set(clientX - innerWidth / 2);
    mouseY.set(clientY - innerHeight / 2);
  };

  // Обработка AnimatedPhrase из Strapi (массив объектов с phrase_text)
  const phrases = Array.isArray(data?.AnimatedPhrase) && data.AnimatedPhrase.length > 0
    ? data.AnimatedPhrase
        .map((p) => p.phrase_text)
        .filter((text): text is string => text !== null && text !== undefined && text.trim() !== "")
    : ["Быстро", "Профессионально", "Надежно"];

  // Логика смены фраз
  useEffect(() => {
    if (phrases.length <= 1) return;
    const timer = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, ANIMATION_CONFIG.phrases.interval);
    return () => clearInterval(timer);
  }, [phrases.length]);

  // Scroll Animation
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const textX = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const tabletX = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const tabletY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const shape1Y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const shape2Y = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const shape3Y = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const tabletImageUrl = data.HeroImage?.url 
    ? getStrapiMedia(data.HeroImage.url)
    : "/tablet_mockup.png";

  return (
    <section 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950 px-6 py-20"
    >
      {/* BACKGROUND DECOR - Floating Shapes Layer */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden bg-[#020617]">
        {/* Deepest Background Layer: Tag Cloud and Data Streams */}
        <TagCloud mouseX={mouseX} mouseY={mouseY} />
        <DataStreamBackground />
        {/* Shape 1 */}
        <motion.div
          style={{ y: shape1Y }}
          animate={{
            x: [0, 200, -150, 0],
            y: [0, -120, 180, 0],
            scale: [1, 1.3, 0.8, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -top-[15%] -left-[10%] w-[60vw] h-[60vw] bg-blue-600/30 rounded-full blur-[100px]"
        />
        
        {/* Shape 2 */}
        <motion.div
          style={{ y: shape2Y }}
          animate={{
            x: [0, -250, 150, 0],
            y: [0, 180, -150, 0],
            scale: [1.1, 0.8, 1.3, 1.1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-[10%] -right-[20%] w-[55vw] h-[55vw] bg-indigo-500/25 rounded-full blur-[90px]"
        />

        {/* Shape 3 */}
        <motion.div
          style={{ y: shape3Y }}
          animate={{
            x: [0, 180, -200, 0],
            y: [0, -220, 120, 0],
            scale: [0.9, 1.25, 0.85, 0.9],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-[0%] left-[10%] w-[48vw] h-[48vw] bg-blue-400/25 rounded-full blur-[110px]"
        />

        {/* Subtle Overlay to blend */}
        <div className="absolute inset-0 bg-slate-950/30 backdrop-blur-[1px]" />
      </div>

      <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-center lg:-mt-24 relative z-10">
        
        {/* Left Content */}
        <motion.div
          style={{ x: textX, opacity: heroOpacity }}
          initial={{ x: -60, opacity: 0 }}
          animate={isExiting ? { x: 0, opacity: 1 } : { x: -60, opacity: 0 }}
          transition={{
            duration: 1.2,
            ease: [0.22, 1, 0.36, 1], // easeOutQuart
            delay: 0.6 // Ждем 0.5с (затухание букв) + 0.1с (начало подъема шторки)
          }}
          className="z-10 text-white space-y-4"
        >
          <div className="flex flex-col">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
              <span className="block text-slate-400 mb-1">{data.StaticTitle}</span>
              <div className="h-[1.15em] relative overflow-hidden mb-[-0.1em]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={phraseIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: ANIMATION_CONFIG.phrases.duration }}
                    className="absolute left-0 top-0 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500 text-3xl md:text-5xl lg:text-6xl leading-[1.1]"
                  >
                    {phrases[phraseIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
            </h1>
          </div>

          <p className="text-lg md:text-xl text-slate-400 max-w-lg font-light leading-snug">
            {data.SubTitle}
          </p>

          <div className="flex flex-wrap gap-4 pt-4">
            <button className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-xl shadow-blue-900/40 hover:scale-105 active:scale-95">
              {data.CTAText || "Начать работу"}
            </button>
            <button className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-bold transition-all backdrop-blur-md">
              {data.SecondaryCTAText || "Узнать больше"}
            </button>
          </div>
        </motion.div>

        {/* Right Content - Tablet */}
        <motion.div
          style={{ x: tabletX, opacity: heroOpacity, y: tabletY }}
          initial={{ x: 60, opacity: 0 }}
          animate={isExiting ? { x: 0, opacity: 1 } : { x: 60, opacity: 0 }}
          transition={{
            duration: 1.2,
            ease: [0.22, 1, 0.36, 1], // easeOutQuart
            delay: 0.7 // Чуть позже текста для эффекта глубины
          }}
          className="relative flex justify-center lg:justify-center"
        >
          {/* Tablet Container */}
          <motion.div
            animate={{ y: [0, -TABLET_LAYOUT_CONFIG.container.levitationY, 0] }}
            transition={ANIMATION_CONFIG.levitation}
            className="relative w-full aspect-square lg:aspect-auto"
            style={{ 
              maxWidth: TABLET_LAYOUT_CONFIG.container.maxWidth,
              rotateZ: TABLET_LAYOUT_CONFIG.container.rotation 
            }}
          >
            {/* Back Glow (Пульсирующая подсветка ЗА планшетом) */}
            <motion.div
              animate={{ 
                scale: TABLET_LAYOUT_CONFIG.backGlow.pulseIntensity,
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                duration: TABLET_LAYOUT_CONFIG.backGlow.pulseSpeed, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="absolute inset-x-0 inset-y-0 rounded-full z-0 pointer-events-none"
              style={{
                backgroundColor: TABLET_LAYOUT_CONFIG.backGlow.color,
                filter: `blur(${TABLET_LAYOUT_CONFIG.backGlow.blur})`,
                transform: `scale(${TABLET_LAYOUT_CONFIG.backGlow.spread})`,
              }}
            />

            {/* Tablet Mockup */}
            <div className="relative z-10 w-full h-full transform-gpu drop-shadow-2xl">
              <Image
                src={tabletImageUrl}
                alt={data.HeroImage?.alternativeText || "Tablet Mockup"}
                width={800}
                height={800}
                priority
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Overlay Canvas (Экран) */}
            <TabletCanvas data={data} />
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
}
